import { BillDetailed, BillVersion } from '@enview/interface/types/bills/Bill';
import moment from 'moment';
import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { g as abilityGlossary } from '../../config/ability';
import { checkPermission } from '../../dux';
import { State, Thunk } from '../../dux/@types';
import { getTeamMode } from '../../dux/TeamModeDux';
import featureFlags, { FrontendFeature } from '../../featureFlags';
import { getBillAlias } from '../../helpers/BillHelper';
import getJurisdictionAdapter from '../../helpers/jurisdictions';
import BillTags from '../Shared/BillTagging/BillTags';
import BillShareButton from '../Shared/BillTracking/BillShareButton';
import BillTrackingButton from '../Shared/BillTracking/BillTrackingButton';
import SingleSelectDropDown, {
  OptionType,
} from '../Shared/DropDowns/SingleSelectDropDown';
import FeedbackIndicator from '../Shared/Icons/FeedbackIndicator';
import SwitchIcon from '../Shared/Icons/SwitchIcon';
import BillAliasField from './BillAliasField';
import BillDetailsActionMenu from './BillDetailsActionMenu';
import './BillView.scss';
import BillViewTitle from './BillViewTitle';
import BillPosition from '../Shared/BillPosition/BillPosition';

type BillViewHeaderProps = {
  bill: BillDetailed;
  selectedDiffValue: boolean;
  selectedBillVersion?: BillVersion;
  changeBillVersion: (option: OptionType<number>) => void;
  changeDiff: (option: OptionType<boolean>) => void;
};

const BillViewHeader = (props: BillViewHeaderProps): ReactElement => {
  const {
    bill,
    selectedDiffValue,
    selectedBillVersion,
    changeBillVersion,
    changeDiff,
  } = props;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const debugModeAvailable = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.BillTextDebug,
    orgUser,
  );
  const billPositionAvailable = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.BillPositionStatement,
    orgUser,
  );
  const teamModeId = useSelector(getTeamMode);
  const alias = getBillAlias(bill.userAliases, teamModeId);

  const jurisdictionAdapter = getJurisdictionAdapter(bill.session.state.abbreviation);
  const sortedBillVersions: BillVersion[] = useMemo(
    () =>
      bill.versions
        ? jurisdictionAdapter.constructor.sortBillVersions(bill.versions)
        : [],
    [bill.versions, jurisdictionAdapter.constructor],
  );

  const { t } = useTranslation();

  const dispatch = useDispatch();
  const canViewMomentum = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.MOMENTUM, true),
    );
  };

  const versionOptions: OptionType<number>[] = sortedBillVersions
    ? sortedBillVersions.map((version) => {
        return {
          label: version.versionPostedDate
            ? `${version.versionName} (${moment
                .utc(version.versionPostedDate)
                .format('M/D/YYYY')})`
            : version.versionName,
          value: version.id,
        };
      })
    : [];

  const changeDiffToggle = (option: boolean): void => {
    const optionType = {
      label: '',
      value: option,
    };
    changeDiff(optionType);
  };

  const getBillVersionControls = (): ReactElement => {
    return (
      <div className="d-flex header-controls flex-wrap flex-md-nowrap header-controls-inline">
        {selectedBillVersion && selectedBillVersion.id && (
          <div className="filter d-flex col-md-8">
            <span className="col-sm-2" style={{ marginRight: '10px' }}>
              {t('bill.header.versionDropdown')}
            </span>
            <div className="col-sm-8">
              <SingleSelectDropDown
                defaultLabel={t('bill.header.billVersionDefaultLabel')}
                onSelect={changeBillVersion}
                options={versionOptions}
                selectedValue={selectedBillVersion ? selectedBillVersion.id : undefined}
              />
            </div>
          </div>
        )}
        <div className="filter d-flex markup-toggle col-md-4">
          <SwitchIcon
            id="bill-markup-toggle"
            onChange={changeDiffToggle}
            value={selectedDiffValue}
          />
          <span>{t('bill.header.showMarkup')}</span>
        </div>
      </div>
    );
  };

  const selectAllText = (e: React.MouseEvent<HTMLTextAreaElement>): void => {
    e.currentTarget.select();
  };

  const hasMomentum = bill.salienceScore === 1;

  return (
    <div className="header d-flex flex-column">
      <div className="flex-row flex-wrap flex-md-nowrap" style={{ height: '60px' }}>
        <BillViewTitle bill={bill} billAlias={alias} />
        <div className="track-button row">
          {hasMomentum && canViewMomentum() && (
            <FeedbackIndicator
              billId={bill.id}
              infoLink={t('urls.momentumInfoLink')}
              place="bottom"
              tooltipDescription={t('bill.header.momentumTooltipMessage')}
              type="momentum"
            />
          )}
          <BillTrackingButton bill={bill} hasStatusIndicator />
          <BillShareButton bill={bill} />
          <BillDetailsActionMenu
            bill={bill}
            selectedBillVersion={selectedBillVersion}
          />
        </div>
      </div>
      <BillAliasField bill={bill} />
      <BillTags bill={bill} hasStatusIndicator showPlaceholderText />
      {billPositionAvailable && <BillPosition bill={bill} showPlaceholderText />}
      {getBillVersionControls()}
      {debugModeAvailable && (
        <textarea
          className="admin-bill-version-id"
          cols={50}
          onClick={selectAllText}
          readOnly
          rows={1}
          value={`${bill && bill.id}::${selectedBillVersion && selectedBillVersion.id}`}
        />
      )}
    </div>
  );
};

export default BillViewHeader;
